import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  User, Bell, Shield, KeyRound, Copy, Loader2, AlertTriangle,
  Mail, Globe2, Languages, Clock4, Monitor, Smartphone, Trash2,
  ShieldCheck, Eye, EyeOff, Send, Sparkles, Settings2, Download,
  PowerOff, CheckCircle2, Info, Heart, Calendar, Database, FileJson,
  Activity, ChevronRight, Lock, Unlock, AlertCircle, Copy as CopyIcon,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Dialog } from '../components/Dialog';
import { supabase } from '../supabaseClient';

/* ===========================================================================
   SettingsPage — redesigned.
   4 cozy tabs:
     - Account          (email, display name, language, timezone)
     - Notifications    (master + 5 individual toggles)
     - API defaults     (per-user defaults for the Playground)
     - Security & Data  (sessions, password, export, delete)
   ======================================================================== */
export default function SettingsPage({ user, onSignOut }) {
  const [tab, setTab] = useState('account');
  return (
    <div className="space-y-6 max-w-5xl">
      <SectionHeader
        title="Settings"
        description="Tweak your account, your alerts, and your default API behaviour."
      />

      <TabBar value={tab} onChange={setTab} />

      {tab === 'account' && <AccountPanel user={user} />}
      {tab === 'notifications' && <NotificationsPanel />}
      {tab === 'api' && <ApiDefaultsPanel />}
      {tab === 'security' && (
        <SecurityPanel user={user} onSignOut={onSignOut} />
      )}
    </div>
  );
}

function TabBar({ value, onChange }) {
  const tabs = [
    { value: 'account', label: 'Account', icon: User, hint: 'Profile + locale' },
    { value: 'notifications', label: 'Notifications', icon: Bell, hint: 'When we email you' },
    { value: 'api', label: 'API defaults', icon: Settings2, hint: 'Playground presets' },
    { value: 'security', label: 'Security & data', icon: ShieldCheck, hint: 'Sessions + privacy' },
  ];
  return (
    <div className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-surface-2 border border-border">
      {tabs.map(({ value, label, icon: Icon, hint }) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          className={`group relative inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
            value === activeValue(value)
              ? 'bg-surface text-fg shadow-sm'
              : 'text-fg-muted hover:text-fg'
          }`}
          title={hint}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
  // tiny local helper so the active-tab styling works
  function activeValue(v) { return value; }
}

/* ===========================================================================
   1. ACCOUNT PANEL
   =========================================================================== */
function AccountPanel({ user }) {
  const meta = user?.user_metadata || {};
  const [email, setEmail] = useState(user?.email || '');
  const [displayName, setDisplayName] = useState(meta.display_name || '');
  const [language, setLanguage] = useState(
    localStorage.getItem('sa.lang') || 'en'
  );
  const [timezone, setTimezone] = useState(
    localStorage.getItem('sa.tz') || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );

  const [emailMsg, setEmailMsg] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  useEffect(() => { localStorage.setItem('sa.lang', language); }, [language]);
  useEffect(() => { localStorage.setItem('sa.tz', timezone); }, [timezone]);

  async function updateEmail(e) {
    e.preventDefault();
    setEmailBusy(true); setEmailMsg(null);
    try {
      const { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;
      setEmailMsg({ type: 'success', text: 'Check your inbox to confirm the new email.' });
    } catch (e) {
      setEmailMsg({ type: 'error', text: e.message });
    } finally {
      setEmailBusy(false);
    }
  }

  async function updateName(e) {
    e.preventDefault();
    setNameBusy(true); setNameMsg(null);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { display_name: displayName.trim() },
      });
      if (error) throw error;
      setNameMsg({ type: 'success', text: 'Display name updated.' });
    } catch (e) {
      setNameMsg({ type: 'error', text: e.message });
    } finally {
      setNameBusy(false);
    }
  }

  const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'ku', label: 'Kurdî (Sorani)' },
    { code: 'ar', label: 'العربية' },
  ];
  const TIMEZONES = [
    'UTC',
    'Asia/Baghdad',
    'Asia/Erbil',
    'Europe/London',
    'Europe/Berlin',
    'America/New_York',
    'America/Los_Angeles',
  ];

  return (
    <div className="space-y-6">
      {/* Display name */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <PillIcon><User className="h-4 w-4 text-brand-strong" /></PillIcon>
            <div>
              <Card.Title>How you appear</Card.Title>
              <Card.Subtitle>The name we greet you with.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <form onSubmit={updateName} className="flex items-end gap-3 max-w-md">
            <Field label="Display name" htmlFor="dn" className="flex-1">
              <Input
                id="dn"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="What should we call you?"
              />
            </Field>
            <Button variant="primary" type="submit" disabled={nameBusy}>
              {nameBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </form>
          {nameMsg && <Toast msg={nameMsg} className="mt-3" />}
        </Card.Body>
      </Card>

      {/* Email */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <PillIcon><Mail className="h-4 w-4 text-brand-strong" /></PillIcon>
            <div>
              <Card.Title>Email</Card.Title>
              <Card.Subtitle>Used for sign-in, receipts, and alerts.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <form onSubmit={updateEmail} className="flex items-end gap-3 max-w-md">
            <Field label="Email" htmlFor="em" className="flex-1">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
                <Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" />
              </div>
            </Field>
            <Button variant="primary" type="submit" disabled={emailBusy}>
              {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
            </Button>
          </form>
          {emailMsg && <Toast msg={emailMsg} className="mt-3" />}
        </Card.Body>
      </Card>

      {/* Locale */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <PillIcon><Globe2 className="h-4 w-4 text-brand-strong" /></PillIcon>
            <div>
              <Card.Title>Locale</Card.Title>
              <Card.Subtitle>Language and timezone for dates & numbers.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Language">
              <div className="relative">
                <Languages className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="Timezone">
              <div className="relative">
                <Clock4 className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </Field>
          </div>
          <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-softest border border-brand-soft text-2xs text-fg-muted">
            <Sparkles className="h-3 w-3 text-brand-strong shrink-0" />
            These are saved locally — they affect how dates appear in this dashboard only.
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

/* ===========================================================================
   2. NOTIFICATIONS PANEL
   =========================================================================== */
function NotificationsPanel() {
  const STORAGE_KEY = 'sa.notify';
  const [prefs, setPrefs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
        usage_80: true,
        usage_100: true,
        usage_exceeded: true,
        payment_failed: true,
        weekly_digest: false,
        product_updates: true,
      };
    } catch {
      return {
        usage_80: true, usage_100: true, usage_exceeded: true,
        payment_failed: true, weekly_digest: false, product_updates: true,
      };
    }
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1200);
    return () => clearTimeout(t);
  }, [prefs]);

  const items = [
    {
      key: 'usage_80',
      title: '80% of credits used',
      desc: 'A friendly heads-up before you hit the wall.',
      icon: AlertCircle,
      tone: 'warning',
    },
    {
      key: 'usage_100',
      title: '100% of credits used',
      desc: 'Final warning — requests will start failing on the next call.',
      icon: AlertTriangle,
      tone: 'destructive',
    },
    {
      key: 'usage_exceeded',
      title: 'Overage blocked',
      desc: 'When a call runs out mid-month and we have to bounce it.',
      icon: PowerOff,
      tone: 'destructive',
    },
    {
      key: 'payment_failed',
      title: 'Payment failed',
      desc: 'Crypto payment didn\'t confirm in time.',
      icon: AlertTriangle,
      tone: 'destructive',
    },
    {
      key: 'product_updates',
      title: 'Product updates',
      desc: 'New engines, regions, and features. Max 1×/month.',
      icon: Sparkles,
      tone: 'info',
    },
    {
      key: 'weekly_digest',
      title: 'Weekly digest',
      desc: 'Every Monday morning: a summary of last week\'s usage.',
      icon: Calendar,
      tone: 'info',
    },
  ];

  const allOn = items.every((i) => prefs[i.key]);
  const allOff = items.every((i) => !prefs[i.key]);

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2 w-full">
          <PillIcon><Bell className="h-4 w-4 text-brand-strong" /></PillIcon>
          <div className="flex-1">
            <Card.Title>Email notifications</Card.Title>
            <Card.Subtitle>Sent to {localStorage.getItem('sa.last_email') || 'your account email'}.</Card.Subtitle>
          </div>
          <div className="flex items-center gap-2">
            {saved && (
              <span className="inline-flex items-center gap-1 text-2xs text-success">
                <CheckCircle2 className="h-3 w-3" /> Saved
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                const newVal = !allOn;
                const next = {};
                items.forEach((i) => { next[i.key] = newVal; });
                setPrefs(next);
              }}
              className="text-2xs font-medium text-brand hover:underline"
            >
              {allOn ? 'Turn all off' : allOff ? 'Turn all on' : 'Turn all on'}
            </button>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="divide-y divide-border">
        {items.map(({ key, title, desc, icon: Icon, tone }) => (
          <NotifRow
            key={key}
            icon={Icon}
            tone={tone}
            title={title}
            desc={desc}
            checked={!!prefs[key]}
            onChange={(v) => setPrefs((p) => ({ ...p, [key]: v }))}
          />
        ))}
      </Card.Body>
    </Card>
  );
}

function NotifRow({ icon: Icon, title, desc, checked, onChange, tone }) {
  const toneDot = {
    warning: 'bg-warning', destructive: 'bg-destructive', info: 'bg-info',
  }[tone] || 'bg-fg-subtle';
  return (
    <div className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0">
      <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center bg-surface-2 border border-border`}>
        <Icon className="h-4 w-4 text-fg-muted" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-fg">{title}</div>
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${toneDot}`} aria-hidden="true" />
        </div>
        <div className="text-xs text-fg-muted mt-0.5">{desc}</div>
      </div>
      <Toggle on={checked} onChange={onChange} />
    </div>
  );
}

/* ===========================================================================
   3. API DEFAULTS PANEL — saved to localStorage, applied to Playground
   =========================================================================== */
const API_DEFAULTS_KEY = 'sa.api_defaults';

const DEFAULT_API_DEFAULTS = {
  proxy_mode: '',           // '' = dynamic; 'classic' or 'stealth'
  country: '',              // 2-letter country code
  response_type: 'html',
  screenshot_format: 'png',
  screenshot_quality: 90,
  render_js: false,
  block_resources: true,
  permissive_status: false,
};

function ApiDefaultsPanel() {
  const [d, setD] = useState(() => {
    try { return { ...DEFAULT_API_DEFAULTS, ...JSON.parse(localStorage.getItem(API_DEFAULTS_KEY) || '{}') }; }
    catch { return DEFAULT_API_DEFAULTS; }
  });
  const [saved, setSaved] = useState(false);

  function update(patch) {
    const next = { ...d, ...patch };
    setD(next);
    localStorage.setItem(API_DEFAULTS_KEY, JSON.stringify(next));
    setSaved(true);
    setTimeout(() => setSaved(false), 1200);
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2 w-full">
            <PillIcon><Settings2 className="h-4 w-4 text-brand-strong" /></PillIcon>
            <div className="flex-1">
              <Card.Title>API defaults</Card.Title>
              <Card.Subtitle>
                Pre-fill these in the Playground so you don't repeat yourself.
              </Card.Subtitle>
            </div>
            {saved && (
              <span className="inline-flex items-center gap-1 text-2xs text-success">
                <CheckCircle2 className="h-3 w-3" /> Saved
              </span>
            )}
          </div>
        </Card.Header>
        <Card.Body className="space-y-5">
          {/* Proxy mode chips */}
          <div>
            <Label icon={ShieldCheck}>Proxy mode</Label>
            <ChipGroup
              value={d.proxy_mode}
              onChange={(v) => update({ proxy_mode: v })}
              options={[
                { value: '',         label: 'Dynamic', sub: 'Server picks', icon: Sparkles },
                { value: 'classic',  label: 'Classic',  sub: 'TLS + residential', icon: ShieldCheck },
                { value: 'stealth',  label: 'Stealth',  sub: 'Datames bypasses CF', icon: Eye },
              ]}
            />
          </div>

          {/* Country + Response type */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Proxy country" hint="2-letter code, optional">
              <div className="relative">
                <Globe2 className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
                <Input
                  value={d.country}
                  onChange={(e) => update({ country: e.target.value.toLowerCase().slice(0, 2) })}
                  placeholder="us, de, iq…"
                  className="pl-9 font-mono"
                  maxLength={2}
                />
              </div>
            </Field>
            <Field label="Response type">
              <select
                value={d.response_type}
                onChange={(e) => update({ response_type: e.target.value })}
                className="w-full h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
              >
                <option value="html">HTML</option>
                <option value="text">Text</option>
                <option value="markdown">Markdown</option>
                <option value="pdf">PDF</option>
              </select>
            </Field>
          </div>

          {/* Screenshot format + quality */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Screenshot format">
              <ChipGroup
                value={d.screenshot_format}
                onChange={(v) => update({ screenshot_format: v })}
                options={[
                  { value: 'png',  label: 'PNG',  sub: 'Lossless' },
                  { value: 'jpeg', label: 'JPEG', sub: 'Smaller files' },
                ]}
              />
            </Field>
            <Field label="JPEG quality" hint="1-100, only used when format = JPEG">
              <Input
                type="number"
                min={1}
                max={100}
                value={d.screenshot_quality}
                onChange={(e) => update({ screenshot_quality: Math.max(1, Math.min(100, parseInt(e.target.value) || 90)) })}
                className="font-mono"
              />
            </Field>
          </div>

          {/* Toggles */}
          <div className="rounded-xl bg-surface-2/50 border border-border divide-y divide-border">
            <ToggleRow
              icon={Monitor}
              title="Render JavaScript"
              desc="Use Playwright to execute JS on the page before scraping."
              on={d.render_js}
              onChange={(v) => update({ render_js: v })}
            />
            <ToggleRow
              icon={Eye}
              title="Block resources"
              desc="Skip images, fonts, CSS to keep responses small and fast."
              on={d.block_resources}
              onChange={(v) => update({ block_resources: v })}
            />
            <ToggleRow
              icon={ShieldCheck}
              title="Permissive status"
              desc="Return the body even when the target returns 4xx/5xx."
              on={d.permissive_status}
              onChange={(v) => update({ permissive_status: v })}
            />
          </div>

          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-softest border border-brand-soft text-2xs text-fg-muted">
            <Info className="h-3 w-3 text-brand-strong shrink-0" />
            Saved on this device only — these don't sync across browsers or devices.
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

/* ===========================================================================
   4. SECURITY & DATA PANEL
   =========================================================================== */
function SecurityPanel({ user, onSignOut }) {
  const [resetSent, setResetSent] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deletePhrase, setDeletePhrase] = useState('');
  const [exportBusy, setExportBusy] = useState(false);

  async function sendReset() {
    if (!user?.email) return;
    setResetBusy(true);
    try {
      await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: window.location.origin + '/reset',
      });
      setResetSent(true);
      setTimeout(() => setResetSent(false), 4000);
    } finally {
      setResetBusy(false);
    }
  }

  function exportData() {
    setExportBusy(true);
    // Build a JSON dump of what we know. Real implementation would call
    // a backend /me/export endpoint.
    const payload = {
      exported_at: new Date().toISOString(),
      user: { email: user?.email, id: user?.id, metadata: user?.user_metadata },
      prefs: {
        notifications: JSON.parse(localStorage.getItem('sa.notify') || 'null'),
        api_defaults: JSON.parse(localStorage.getItem(API_DEFAULTS_KEY) || 'null'),
        locale: localStorage.getItem('sa.lang'),
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `datames-export-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setTimeout(() => setExportBusy(false), 800);
  }

  return (
    <div className="space-y-6">
      {/* Active session */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <PillIcon><Monitor className="h-4 w-4 text-brand-strong" /></PillIcon>
            <div>
              <Card.Title>This session</Card.Title>
              <Card.Subtitle>You're currently signed in on this device.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <SessionRow
            user={user}
            isCurrent
            onSignOut={onSignOut}
          />
        </Card.Body>
      </Card>

      {/* Password */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <PillIcon><KeyRound className="h-4 w-4 text-brand-strong" /></PillIcon>
            <div>
              <Card.Title>Password</Card.Title>
              <Card.Subtitle>Send yourself a reset link by email.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={sendReset} disabled={resetBusy}>
              {resetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send reset email
            </Button>
            {resetSent && (
              <span className="inline-flex items-center gap-1.5 text-2xs text-success">
                <CheckCircle2 className="h-3 w-3" /> Link sent to {user?.email}
              </span>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Two-factor (placeholder for the future) */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <PillIcon><Shield className="h-4 w-4 text-brand-strong" /></PillIcon>
            <div>
              <Card.Title>Two-factor authentication</Card.Title>
              <Card.Subtitle>Coming soon. We'll let you know when it's ready.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-warning/10 border border-warning/30 text-xs text-warning">
            <Sparkles className="h-3.5 w-3.5 shrink-0" />
            <span>TOTP 2FA is on the roadmap. We won't roll it out without making sure it works smoothly with our email-OTP sign-in flow.</span>
          </div>
        </Card.Body>
      </Card>

      {/* Data export */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <PillIcon><Database className="h-4 w-4 text-brand-strong" /></PillIcon>
            <div>
              <Card.Title>Your data</Card.Title>
              <Card.Subtitle>Download a copy of everything we have on you.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" onClick={exportData} disabled={exportBusy}>
              {exportBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export as JSON
            </Button>
            <div className="text-2xs text-fg-muted">
              Includes your profile, settings, and any API keys (in <span className="font-mono">metadata</span>, not the secret values).
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Danger zone */}
      <Card className="border-destructive/40">
        <Card.Header>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-destructive/10 border border-destructive/30 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
            <div>
              <Card.Title className="text-destructive">Danger zone</Card.Title>
              <Card.Subtitle>Irreversible. Be sure.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 rounded-lg border border-destructive/30 bg-destructive/5">
            <div>
              <div className="text-sm font-medium text-fg">Delete this account</div>
              <div className="text-xs text-fg-muted mt-0.5">
                Wipes your profile, API keys, and remaining credits. Cannot be undone.
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete account
            </Button>
          </div>
        </Card.Body>
      </Card>

      <DeleteAccountDialog
        open={showDelete}
        onClose={() => { setShowDelete(false); setDeletePhrase(''); }}
        expectedPhrase="DELETE"
        typed={deletePhrase}
        onTyped={setDeletePhrase}
      />
    </div>
  );
}

function SessionRow({ user, isCurrent, onSignOut }) {
  const ua = user?.user_metadata?.full_name || user?.email;
  return (
    <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-surface-2 border border-border">
      <div className="h-9 w-9 rounded-lg bg-success/15 border border-success/30 flex items-center justify-center">
        <Smartphone className="h-4 w-4 text-success" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-fg truncate">{user?.email}</div>
          {isCurrent && <Badge variant="success" className="text-2xs">Current</Badge>}
        </div>
        <div className="text-2xs text-fg-muted mt-0.5 flex items-center gap-1.5">
          <Dot variant="success" pulse />
          Active now · last seen just now
        </div>
      </div>
      {isCurrent && (
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          <PowerOff className="h-3.5 w-3.5" />
          Sign out
        </Button>
      )}
    </div>
  );
}

function DeleteAccountDialog({ open, onClose, expectedPhrase, typed, onTyped }) {
  const matches = typed === expectedPhrase;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title="Delete your account?"
      description="This wipes your profile, API keys, and remaining credits. There's no undo."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={!matches}
            title={matches ? '' : `Type "${expectedPhrase}" to confirm`}
            onClick={() => {
              alert('Account deletion requested. We\'ll send a confirmation email.');
              onClose();
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete forever
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            All your <strong>API keys</strong>, <strong>usage history</strong>, and <strong>credits</strong> will be permanently removed.
            Active subscriptions are cancelled, but crypto payments are not refunded.
          </span>
        </div>
        <Field label={`Type "${expectedPhrase}" to confirm`}>
          <Input
            value={typed}
            onChange={(e) => onTyped(e.target.value)}
            placeholder={expectedPhrase}
            autoComplete="off"
            className="font-mono"
          />
        </Field>
      </div>
    </Dialog>
  );
}

/* ===========================================================================
   SHARED ATOMS
   =========================================================================== */
function PillIcon({ children }) {
  return (
    <div className="h-8 w-8 rounded-lg bg-brand-softest border border-brand-soft flex items-center justify-center shrink-0">
      {children}
    </div>
  );
}

function Field({ label, hint, htmlFor, className, children }) {
  return (
    <div className={`space-y-1.5 ${className || ''}`}>
      {label && (
        <label htmlFor={htmlFor} className="text-2xs font-medium text-fg-muted flex items-center justify-between">
          <span>{label}</span>
          {hint && <span className="text-2xs text-fg-subtle">{hint}</span>}
        </label>
      )}
      {children}
    </div>
  );
}

function Label({ icon: Icon, children }) {
  return (
    <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider flex items-center gap-1.5 mb-2">
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </div>
  );
}

function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
        on ? 'bg-brand' : 'bg-surface-3'
      }`}
    >
      <span
        aria-hidden="true"
        className="absolute top-0.5 h-4 w-4 rounded-full bg-offwhite shadow transition-[left] duration-200 ease-out"
        style={{ left: on ? '18px' : '2px' }}
      />
    </button>
  );
}

function ToggleRow({ icon: Icon, title, desc, on, onChange }) {
  return (
    <div className="flex items-center gap-4 py-3.5 px-3 first:pt-3 last:pb-3">
      <div className="shrink-0 h-9 w-9 rounded-lg bg-surface border border-border flex items-center justify-center">
        <Icon className="h-4 w-4 text-fg-muted" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-fg">{title}</div>
        <div className="text-xs text-fg-muted mt-0.5">{desc}</div>
      </div>
      <Toggle on={on} onChange={onChange} />
    </div>
  );
}

function ChipGroup({ value, onChange, options }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {options.map((opt) => {
        const on = (value || '') === (opt.value || '');
        const Icon = opt.icon;
        return (
          <button
            key={opt.value || '_empty'}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`text-left p-3 rounded-xl border transition-all ${
              on
                ? 'bg-brand-softest border-brand-soft ring-1 ring-brand/30'
                : 'bg-surface-2/40 border-border hover:bg-surface-2 hover:border-fg-subtle'
            }`}
          >
            <div className="flex items-center gap-2">
              {Icon && <Icon className={`h-3.5 w-3.5 ${on ? 'text-brand-strong' : 'text-fg-muted'}`} />}
              <div className={`text-xs font-semibold ${on ? 'text-brand-strong' : 'text-fg'}`}>
                {opt.label}
              </div>
              {on && <CheckCircle2 className="h-3 w-3 text-brand ml-auto" />}
            </div>
            {opt.sub && (
              <div className="text-2xs text-fg-muted mt-1 ml-5.5">{opt.sub}</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Toast({ msg, className = '' }) {
  return (
    <div className={`px-3 py-2 rounded-md text-xs border ${
      msg.type === 'success'
        ? 'bg-success/10 border-success/30 text-success'
        : 'bg-destructive/10 border-destructive/30 text-destructive'
    } ${className}`}>
      {msg.text}
    </div>
  );
}